import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useActivateProsProfile,
  useAgreeToProviderTerms,
  useDeactivateProsProfile,
  useMyProsProfile,
  useMyProsBusinesses,
  useRequestCoachWorkspace,
  useTenantBootstrap,
  useUploadProsProfilePhoto,
  useUpdateProsAcademyDetails,
  useUpdateProsCoachDetails,
  useUpdateProsProfile,
  useUpdateProsStringerDetails,
} from '@tark/shared/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Textarea } from '@tark/shared/components';
import CoachPageHeader from '../components/CoachPageHeader';
import type {
  ProviderListingType,
  ProviderProfile,
  ProviderPublicationStatus,
  ProviderWorkspaceStatus,
  TenantMembershipSummary,
} from '@tark/shared/types';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRightLeft,
  Building2,
  CheckCircle2,
  Clock3,
  Image,
  Loader2,
  Plus,
  Sparkles,
  Upload,
  UserRound,
  Wrench,
} from 'lucide-react';

function splitCsv(value: string): string[] | undefined {
  const items = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length ? items : undefined;
}

function toNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function getPrimaryType(profile: ProviderProfile): ProviderListingType {
  return profile.providerTypes[0] ?? 'COACH';
}

function getTypeIcon(type: ProviderListingType) {
  switch (type) {
    case 'ACADEMY':
      return Building2;
    case 'STRINGER':
      return Wrench;
    default:
      return UserRound;
  }
}

function getStatusLabel(status?: ProviderPublicationStatus | null) {
  switch (status) {
    case 'DRAFT':
      return 'Draft';
    case 'SUBMITTED':
      return 'Pending review';
    case 'VERIFIED':
      return 'Approved';
    case 'PUBLISHED':
      return 'Live';
    case 'SUSPENDED':
      return 'Needs clarification';
    case 'ARCHIVED':
      return 'Rejected / closed';
    default:
      return 'Draft';
  }
}

function getWorkspaceStatusLabel(status?: ProviderWorkspaceStatus | string | null) {
  switch (status) {
    case 'REQUESTED':
      return 'Requested';
    case 'APPROVED':
      return 'Approved';
    case 'ENABLED':
      return 'Enabled';
    case 'DECLINED':
      return 'Declined';
    case 'CANCELLED':
      return 'Cancelled';
    case 'NOT_REQUESTED':
    default:
      return 'Not enabled';
  }
}

const PROVIDER_ROLE_OPTIONS: Array<{
  type: ProviderListingType;
  label: string;
  description: string;
}> = [
  {
    type: 'STRINGER',
    label: 'Stringer',
    description: 'Restringing, setup support, and racquet services.',
  },
  {
    type: 'COACH',
    label: 'Coach',
    description: 'Lessons, clinics, and player development.',
  },
  {
    type: 'ACADEMY',
    label: 'Academy',
    description: 'Programs, facilities, and public academy listings.',
  },
];

function getTypeLabel(type: ProviderListingType) {
  switch (type) {
    case 'ACADEMY':
      return 'Academy';
    case 'STRINGER':
      return 'Stringer';
    default:
      return 'Coach';
  }
}

function buildHealthChecks(profile: ProviderProfile) {
  const hasContactMethod = Boolean(
    profile.contactPhone || profile.contactEmail || profile.contactWhatsapp || profile.websiteUrl
  );
  const hasSports = Boolean(profile.sports?.length);
  const hasBio = Boolean(profile.bio?.trim());
  const hasLocation = Boolean(profile.city?.trim() && profile.state?.trim() && profile.zipcode?.trim());
  const hasPhoto = Boolean(profile.profilePhotoUrl?.trim());
  const termsAccepted = profile.publicationStatus !== 'DRAFT';

  const checks = [
    { label: 'Public display name', ready: Boolean(profile.displayName?.trim()) },
    { label: 'Terms accepted', ready: termsAccepted },
    { label: 'Contact method', ready: hasContactMethod },
    { label: 'Location', ready: hasLocation },
    { label: 'Sports tags', ready: hasSports },
    { label: 'Bio', ready: hasBio },
    { label: 'Profile photo', ready: hasPhoto },
  ];

  if (profile.providerTypes.includes('STRINGER')) {
    checks.push({
      label: 'Stringer details',
      ready: Boolean(
        profile.stringerDetails?.basePriceMin ||
          profile.stringerDetails?.turnaroundHours ||
          profile.stringerDetails?.stringBrands?.length
      ),
    });
  }

  if (profile.providerTypes.includes('COACH')) {
    checks.push({
      label: 'Coach details',
      ready: Boolean(
        profile.coachDetails?.hourlyRateMin ||
          profile.coachDetails?.lessonTypes?.length ||
          profile.coachDetails?.specialties?.length
      ),
    });
  }

  if (profile.providerTypes.includes('ACADEMY')) {
    checks.push({
      label: 'Academy details',
      ready: Boolean(
        profile.academyDetails?.academyName ||
          profile.academyDetails?.programsOffered?.length ||
          profile.academyDetails?.numCourts
      ),
    });
  }

  return checks;
}

function getReadinessLabel(profile: ProviderProfile) {
  switch (profile.readinessStatus) {
    case 'READY':
      return 'Ready';
    case 'BLOCKED':
      return 'Blocked';
    case 'NEEDS_REVIEW':
      return 'Needs review';
    default:
      return profile.readyToPublish ? 'Ready' : 'Needs review';
  }
}

function getStatusGuidance(profile: ProviderProfile) {
  if (profile.publicationStatus === 'DRAFT') {
    return {
      title: 'Draft',
      description: 'Finish the checklist, then submit for TARK review.',
    };
  }
  if (profile.publicationStatus === 'SUBMITTED') {
    return {
      title: 'In review',
      description: 'TARK is reviewing this profile.',
    };
  }
  if (profile.publicationStatus === 'VERIFIED') {
    return {
      title: 'Approved',
      description: 'Ready to go live in TARK PROs.',
    };
  }
  if (profile.publicationStatus === 'PUBLISHED' && profile.isActive) {
    return {
      title: 'Live',
      description: 'Visible in TARK PROs.',
    };
  }
  if (profile.publicationStatus === 'PUBLISHED') {
    return {
      title: 'Paused',
      description: 'Approved, but hidden from TARK PROs.',
    };
  }
  if (profile.publicationStatus === 'SUSPENDED') {
    return {
      title: 'Needs clarification',
      description: 'TARK needs follow-up before this listing can return to public browse.',
    };
  }
  return {
    title: 'Rejected / closed',
    description: 'This request is closed. Contact TARK support if you want to reopen it.',
  };
}

function getProviderWorkflowStepIndex(status?: ProviderPublicationStatus | null) {
  switch (status) {
    case 'SUBMITTED':
    case 'SUSPENDED':
    case 'ARCHIVED':
      return 1;
    case 'VERIFIED':
      return 2;
    case 'PUBLISHED':
      return 3;
    case 'DRAFT':
    default:
      return 0;
  }
}

function findLinkedTenantMembership(
  profile: ProviderProfile,
  memberships: TenantMembershipSummary[]
): TenantMembershipSummary | null {
  if (!profile.tenantId) {
    return null;
  }
  return memberships.find((membership) => membership.tenant?.id === profile.tenantId) ?? null;
}

export default function ProsManagePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawId = params.get('providerId');
  const tenantId = params.get('tenantId');
  const businesses = useMyProsBusinesses(rawId === null && tenantId !== null);
  const scopedListing = businesses.data?.find((business) => String(business.tenantId) === tenantId);
  const providerId = rawId !== null ? Number(rawId) : tenantId !== null ? scopedListing?.id : undefined;
  const validId = rawId !== null ? /^[1-9][0-9]*$/.test(rawId) && Number.isSafeInteger(providerId) : tenantId === null || scopedListing !== undefined;
  const { data: profile, isLoading, refetch } = useMyProsProfile(validId, providerId);
  const {
    activeTenant,
    memberships,
    selectTenant,
    isSwitchingTenant,
  } = useTenantBootstrap(Boolean(profile));
  const updateProfile = useUpdateProsProfile(providerId);
  const updateStringer = useUpdateProsStringerDetails(providerId);
  const updateCoach = useUpdateProsCoachDetails(providerId);
  const updateAcademy = useUpdateProsAcademyDetails(providerId);
  const uploadProfilePhoto = useUploadProsProfilePhoto(providerId);
  const agreeToTerms = useAgreeToProviderTerms(providerId);
  const activateProfile = useActivateProsProfile(providerId);
  const deactivateProfile = useDeactivateProsProfile(providerId);
  const requestCoachWorkspace = useRequestCoachWorkspace(providerId);
  const profilePhotoInputRef = useRef<HTMLInputElement | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [city, setCity] = useState('');
  const [locationState, setLocationState] = useState('');
  const [bio, setBio] = useState('');
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  const [sports, setSports] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [serviceRadiusMiles, setServiceRadiusMiles] = useState('');

  const [stringerExperienceYears, setStringerExperienceYears] = useState('');
  const [stringerTurnaroundHours, setStringerTurnaroundHours] = useState('');
  const [stringerBasePriceMin, setStringerBasePriceMin] = useState('');
  const [stringerBasePriceMax, setStringerBasePriceMax] = useState('');
  const [stringerBrands, setStringerBrands] = useState('');

  const [coachExperienceYears, setCoachExperienceYears] = useState('');
  const [coachRateMin, setCoachRateMin] = useState('');
  const [coachRateMax, setCoachRateMax] = useState('');
  const [coachLessonTypes, setCoachLessonTypes] = useState('');
  const [coachSpecialties, setCoachSpecialties] = useState('');
  const [coachAvailability, setCoachAvailability] = useState('');

  const [academyName, setAcademyName] = useState('');
  const [academyCourts, setAcademyCourts] = useState('');
  const [academyPrograms, setAcademyPrograms] = useState('');
  const [stagedTypes, setStagedTypes] = useState<ProviderListingType[]>([]);

  useEffect(() => {
    if (!profile) return;
    setDisplayName(profile.displayName ?? '');
    setZipcode(profile.zipcode ?? '');
    setCity(profile.city ?? '');
    setLocationState(profile.state ?? '');
    setBio(profile.bio ?? '');
    setProfilePhotoUrl(profile.profilePhotoUrl ?? '');
    setSports(profile.sports?.join(', ') ?? '');
    setContactPhone(profile.contactPhone ?? '');
    setContactEmail(profile.contactEmail ?? '');
    setWebsiteUrl(profile.websiteUrl ?? '');
    setServiceRadiusMiles(profile.serviceRadiusMiles != null ? String(profile.serviceRadiusMiles) : '');

    setStringerExperienceYears(profile.stringerDetails?.experienceYears != null ? String(profile.stringerDetails.experienceYears) : '');
    setStringerTurnaroundHours(profile.stringerDetails?.turnaroundHours != null ? String(profile.stringerDetails.turnaroundHours) : '');
    setStringerBasePriceMin(profile.stringerDetails?.basePriceMin != null ? String(profile.stringerDetails.basePriceMin) : '');
    setStringerBasePriceMax(profile.stringerDetails?.basePriceMax != null ? String(profile.stringerDetails.basePriceMax) : '');
    setStringerBrands(profile.stringerDetails?.stringBrands?.join(', ') ?? '');

    setCoachExperienceYears(profile.coachDetails?.experienceYears != null ? String(profile.coachDetails.experienceYears) : '');
    setCoachRateMin(profile.coachDetails?.hourlyRateMin != null ? String(profile.coachDetails.hourlyRateMin) : '');
    setCoachRateMax(profile.coachDetails?.hourlyRateMax != null ? String(profile.coachDetails.hourlyRateMax) : '');
    setCoachLessonTypes(profile.coachDetails?.lessonTypes?.join(', ') ?? '');
    setCoachSpecialties(profile.coachDetails?.specialties?.join(', ') ?? '');
    setCoachAvailability(profile.coachDetails?.availabilityNotes ?? '');

    setAcademyName(profile.academyDetails?.academyName ?? '');
    setAcademyCourts(profile.academyDetails?.numCourts != null ? String(profile.academyDetails.numCourts) : '');
    setAcademyPrograms(profile.academyDetails?.programsOffered?.join(', ') ?? '');
    setStagedTypes((current) => current.filter((type) => !profile.providerTypes.includes(type)));
  }, [profile]);

  const leadType = profile ? getPrimaryType(profile) : 'COACH';
  const LeadTypeIcon = getTypeIcon(leadType);
  const healthChecks = profile ? buildHealthChecks(profile) : [];
  const statusGuidance = profile ? getStatusGuidance(profile) : null;
  const editableTypes = Array.from(new Set([...(profile?.providerTypes ?? []), ...stagedTypes])) as ProviderListingType[];
  const availableTypes = profile
    ? PROVIDER_ROLE_OPTIONS.filter((option) => !editableTypes.includes(option.type))
    : PROVIDER_ROLE_OPTIONS;
  const linkedTenantMembership = useMemo(
    () => (profile ? findLinkedTenantMembership(profile, memberships) : null),
    [memberships, profile]
  );

  const handleOpenLinkedWorkspace = async (targetRoute: string = '/welcome') => {
    const tenantId = linkedTenantMembership?.tenant?.id ?? profile?.tenantId ?? null;
    if (!tenantId) {
      navigate(targetRoute);
      return;
    }
    if (activeTenant?.tenant?.id !== tenantId) {
      await selectTenant({ tenantId });
    }
    navigate(targetRoute);
  };

  const handleSaveCore = async () => {
    if (!profile) return;
    setError(null);
    setSuccess(null);
    try {
      await updateProfile.mutateAsync({
        displayName: displayName.trim() || undefined,
        zipcode: zipcode.trim() || undefined,
        city: city.trim() || undefined,
        state: locationState.trim() || undefined,
        bio: bio.trim() || undefined,
        profilePhotoUrl: profilePhotoUrl.trim(),
        sports: splitCsv(sports),
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        websiteUrl: websiteUrl.trim() || undefined,
        serviceRadiusMiles: toNumber(serviceRadiusMiles),
      });
      await refetch();
      setSuccess('Listing details saved.');
    } catch (submitError: any) {
      setError(submitError?.response?.data?.message || submitError?.message || 'Could not save the listing details.');
    }
  };

  const handleProfilePhotoSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError(null);
    setSuccess(null);

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Choose a JPG, PNG, or WebP image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Profile photo must be under 10 MB.');
      return;
    }

    try {
      const updated = await uploadProfilePhoto.mutateAsync(file);
      setProfilePhotoUrl(updated.profilePhotoUrl ?? '');
      await refetch();
      setSuccess('Profile photo uploaded.');
    } catch (submitError: any) {
      setError(submitError?.response?.data?.message || submitError?.message || 'Could not upload the profile photo.');
    }
  };

  const handleSaveTypeDetails = async (type: ProviderListingType) => {
    if (!profile) return;
    setError(null);
    setSuccess(null);
    try {
      const addingNewType = !profile.providerTypes.includes(type);

      if (type === 'STRINGER') {
        await updateStringer.mutateAsync({
          experienceYears: toNumber(stringerExperienceYears),
          turnaroundHours: toNumber(stringerTurnaroundHours),
          basePriceMin: toNumber(stringerBasePriceMin),
          basePriceMax: toNumber(stringerBasePriceMax),
          stringBrands: splitCsv(stringerBrands),
        });
      } else if (type === 'COACH') {
        await updateCoach.mutateAsync({
          experienceYears: toNumber(coachExperienceYears),
          hourlyRateMin: toNumber(coachRateMin),
          hourlyRateMax: toNumber(coachRateMax),
          lessonTypes: splitCsv(coachLessonTypes),
          specialties: splitCsv(coachSpecialties),
          availabilityNotes: coachAvailability.trim() || undefined,
        });
      } else {
        await updateAcademy.mutateAsync({
          academyName: academyName.trim() || undefined,
          numCourts: toNumber(academyCourts),
          programsOffered: splitCsv(academyPrograms),
        });
      }
      await refetch();
      if (addingNewType) {
        setStagedTypes((current) => current.filter((item) => item !== type));
        setSuccess(`${getTypeLabel(type)} role added. Because the Public Profile changed, it is back in review until TARK approves the update.`);
      } else {
        setSuccess(`${getTypeLabel(type)} details saved.`);
      }
    } catch (submitError: any) {
      setError(submitError?.response?.data?.message || submitError?.message || 'Could not save the provider-specific details.');
    }
  };

  const handleSubmitForReview = async () => {
    setError(null);
    setSuccess(null);
    try {
      await agreeToTerms.mutateAsync('1.0');
      await refetch();
      setSuccess('Listing submitted for TARK review.');
    } catch (submitError: any) {
      setError(submitError?.response?.data?.message || submitError?.message || 'Could not submit the listing for review.');
    }
  };

  const handleToggleLive = async () => {
    if (!profile) return;
    setError(null);
    setSuccess(null);
    try {
      if (profile.isActive) {
        await deactivateProfile.mutateAsync();
        setSuccess('Listing paused from public browse.');
      } else {
        await activateProfile.mutateAsync();
        setSuccess('Listing is now live in PROs.');
      }
      await refetch();
    } catch (submitError: any) {
      setError(submitError?.response?.data?.message || submitError?.message || 'Could not change the listing visibility.');
    }
  };

  const handleRequestCoachWorkspace = async () => {
    setError(null);
    setSuccess(null);
    try {
      await requestCoachWorkspace.mutateAsync(undefined);
      await refetch();
      setSuccess('Business workspace requested.');
    } catch (submitError: any) {
      setError(submitError?.response?.data?.message || submitError?.message || 'Could not request business workspace.');
    }
  };

  if (tenantId && businesses.isLoading) return <p>Loading business...</p>;
  if (tenantId && !scopedListing) return <p>You do not own the listing for this business.</p>;
  if (!validId) return <p>Invalid business link.</p>;

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <Card variant="default" className="max-w-2xl">
        <CardHeader>
          <CardTitle>No Public Profile yet</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Link to="/welcome">
            <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />}>Home</Button>
          </Link>
          <Link to="/pros/become">
            <Button>Create Profile</Button>
          </Link>
          <Link to="/pros">
            <Button variant="outline">Back to PROs</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const actionLoading =
    agreeToTerms.isPending || activateProfile.isPending || deactivateProfile.isPending;
  const readyCount = healthChecks.filter((check) => check.ready).length;
  const missingChecks = healthChecks.filter((check) => !check.ready);
  const issueCount = (profile.blockingIssues?.length ?? 0) + (profile.warningIssues?.length ?? 0);
  const readinessValue = `${readyCount}/${healthChecks.length}`;
  const listingTitle = displayName.trim() || profile.displayName || 'Public Profile';
  const primaryIssue =
    profile.blockingIssues?.[0] ||
    profile.warningIssues?.[0] ||
    missingChecks[0]?.label ||
    (profile.readyToPublish ? 'Ready for admin publish' : 'Ready to review');

  return (
    <div className="w-full max-w-none space-y-6 pb-24 md:pb-8">
      <CoachPageHeader
        title="Public Profile"
        subtitle="Manage the marketplace profile families see before they contact or register."
        meta={
          <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">
            {listingTitle}
          </span>
        }
        action={
          <Link to={profile.id ? `/pros/provider/${profile.id}` : '/pros'}>
            <Button variant="outline">Preview Public Profile</Button>
          </Link>
        }
      />

      <section className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
        <div className="h-1 bg-primary" />
        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_380px] 2xl:grid-cols-[minmax(0,1fr)_420px]">
          <div className="space-y-5 p-5 md:p-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                <LeadTypeIcon className="h-3.5 w-3.5" />
                {getTypeLabel(leadType)}
              </span>
              <span className="rounded-full border border-border bg-surface px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-text-muted">
                {getStatusLabel(profile.publicationStatus)}
              </span>
              {profile.publiclyVisible ? (
                <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                  Public
                </span>
              ) : null}
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Manage Profile</p>
              <h1 className="mt-1 text-3xl font-semibold tracking-tight text-text">{listingTitle}</h1>
              <p className="mt-2 text-sm font-medium text-text-muted">
                {getTypeLabel(leadType)} Public Profile
              </p>
            </div>

            {profile.publicationStatus !== 'PUBLISHED' ? (
              <ProviderReviewWorkflow profile={profile} />
            ) : null}

            <div className="flex flex-wrap gap-3">
              {profile.publicationStatus === 'DRAFT' ? (
                <Button
                  onClick={handleSubmitForReview}
                  isLoading={actionLoading}
                >
                  Submit for Review
                </Button>
              ) : null}
              {(profile.publicationStatus === 'VERIFIED' || profile.publicationStatus === 'PUBLISHED') && profile.isActive ? (
                <Button variant="outline" onClick={handleToggleLive} isLoading={actionLoading}>
                  Pause Listing
                </Button>
              ) : null}
              {(profile.publicationStatus === 'VERIFIED' || profile.publicationStatus === 'PUBLISHED') && !profile.isActive ? (
                <Button onClick={handleToggleLive} isLoading={actionLoading}>
                  Go Live
                </Button>
              ) : null}
            </div>
          </div>

          <aside className="border-t border-border bg-surface p-5 lg:border-l lg:border-t-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-subtle">Profile status</p>
            <p className="mt-2 text-2xl font-semibold text-text">{getReadinessLabel(profile)}</p>
            <p className="mt-1 text-sm font-semibold text-text-muted">{statusGuidance?.title || primaryIssue}</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border bg-white px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-subtle">Checklist</p>
                <p className="mt-1.5 text-xl font-semibold text-text">{readinessValue}</p>
              </div>
              <div className="rounded-lg border border-border bg-white px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-subtle">Needs</p>
                <p className={issueCount > 0 ? 'mt-1.5 text-xl font-semibold text-amber-700' : 'mt-1.5 text-xl font-semibold text-primary'}>
                  {issueCount || missingChecks.length}
                </p>
              </div>
            </div>
            {missingChecks.length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {missingChecks.slice(0, 5).map((check) => (
                  <span key={check.label} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
                    {check.label}
                  </span>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-primary/20 bg-primary/10 px-4 py-3 text-sm font-semibold text-primary">
                Checklist complete
              </div>
            )}
            <Button className="mt-5 w-full" variant="outline" onClick={handleSaveCore} isLoading={updateProfile.isPending}>
              Save Public Profile
            </Button>
          </aside>
        </div>
      </section>

      {error ? (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-text">
          {success}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,420px)] 2xl:grid-cols-[minmax(0,1fr)_440px]">
        <div className="space-y-6">
          <Card variant="default">
            <CardHeader>
              <CardTitle>Public details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-4 rounded-lg border border-border bg-surface-elevated/20 p-4 sm:flex-row sm:items-center">
                <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary/10 text-primary">
                  {profilePhotoUrl.trim() ? (
                    <img
                      src={profilePhotoUrl.trim()}
                      alt={displayName || 'Profile photo'}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Image className="h-8 w-8" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-text">Profile photo</p>
                      <p className="mt-1 text-xs font-medium text-text-muted">
                        Required for review. JPG, PNG, or WebP.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      leftIcon={uploadProfilePhoto.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      onClick={() => profilePhotoInputRef.current?.click()}
                      disabled={uploadProfilePhoto.isPending}
                    >
                      {uploadProfilePhoto.isPending ? 'Uploading' : 'Choose Image'}
                    </Button>
                    <input
                      ref={profilePhotoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleProfilePhotoSelect}
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {profilePhotoUrl.trim() ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setProfilePhotoUrl('')}
                      >
                        Clear Photo
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <SectionKicker title="Identity" />
                <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
                  <Input label="Display Name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
                  <Input
                    label="Sports"
                    value={sports}
                    onChange={(event) => setSports(event.target.value)}
                    placeholder="tennis, pickleball"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <SectionKicker title="Location" />
                <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
                  <Input label="ZIP Code" value={zipcode} onChange={(event) => setZipcode(event.target.value)} placeholder="75071" />
                  <Input label="City" value={city} onChange={(event) => setCity(event.target.value)} placeholder="McKinney" />
                  <Input label="State" value={locationState} onChange={(event) => setLocationState(event.target.value)} placeholder="TX" />
                  <Input
                    label="Service Radius"
                    value={serviceRadiusMiles}
                    onChange={(event) => setServiceRadiusMiles(event.target.value)}
                    placeholder="25"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <SectionKicker title="Contact" />
                <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
                  <Input label="Phone" value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} />
                  <Input label="Email" value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} />
                  <Input label="Website" value={websiteUrl} onChange={(event) => setWebsiteUrl(event.target.value)} />
                </div>
              </div>

              <Textarea
                label="Bio"
                value={bio}
                onChange={(event) => setBio(event.target.value)}
                placeholder="What do you coach, who do you help, and where do you teach?"
              />
              <Button
                onClick={handleSaveCore}
                isLoading={updateProfile.isPending}
              >
                Save Public Profile
              </Button>
            </CardContent>
          </Card>

          <Card variant="default">
            <CardHeader>
              <CardTitle>Services</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {editableTypes.map((type) => (
                <div key={type} className="space-y-4 rounded-2xl border border-border bg-surface-elevated/20 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-text">{getTypeLabel(type)} details</p>
                    </div>
                    {!profile.providerTypes.includes(type) ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                        <Plus className="h-3.5 w-3.5" />
                        New role
                      </span>
                    ) : null}
                  </div>

                  {type === 'STRINGER' ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      <Input label="Experience (years)" value={stringerExperienceYears} onChange={(event) => setStringerExperienceYears(event.target.value)} />
                      <Input label="Turnaround (hours)" value={stringerTurnaroundHours} onChange={(event) => setStringerTurnaroundHours(event.target.value)} />
                      <Input label="Base Price Min" value={stringerBasePriceMin} onChange={(event) => setStringerBasePriceMin(event.target.value)} />
                      <Input label="Base Price Max" value={stringerBasePriceMax} onChange={(event) => setStringerBasePriceMax(event.target.value)} />
                      <div className="md:col-span-2">
                        <Input
                          label="String Brands"
                          value={stringerBrands}
                          onChange={(event) => setStringerBrands(event.target.value)}
                          placeholder="Luxilon, Babolat, Wilson"
                        />
                      </div>
                    </div>
                  ) : null}

                  {type === 'COACH' ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      <Input label="Experience (years)" value={coachExperienceYears} onChange={(event) => setCoachExperienceYears(event.target.value)} />
                      <Input label="Rate Min" value={coachRateMin} onChange={(event) => setCoachRateMin(event.target.value)} />
                      <Input label="Rate Max" value={coachRateMax} onChange={(event) => setCoachRateMax(event.target.value)} />
                      <Input
                        label="Lesson Types"
                        value={coachLessonTypes}
                        onChange={(event) => setCoachLessonTypes(event.target.value)}
                        placeholder="Private, Group, Clinic"
                      />
                      <div className="md:col-span-2">
                        <Input
                          label="Specialties"
                          value={coachSpecialties}
                          onChange={(event) => setCoachSpecialties(event.target.value)}
                          placeholder="Junior development, technique, match play"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Textarea
                          label="Availability Notes"
                          value={coachAvailability}
                          onChange={(event) => setCoachAvailability(event.target.value)}
                          placeholder="Weekday evenings, weekend mornings, etc."
                        />
                      </div>
                    </div>
                  ) : null}

                  {type === 'ACADEMY' ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      <Input label="Academy Name" value={academyName} onChange={(event) => setAcademyName(event.target.value)} />
                      <Input label="Court Count" value={academyCourts} onChange={(event) => setAcademyCourts(event.target.value)} />
                      <div className="md:col-span-2">
                        <Input
                          label="Programs Offered"
                          value={academyPrograms}
                          onChange={(event) => setAcademyPrograms(event.target.value)}
                          placeholder="Junior development, camps, adult clinics"
                        />
                      </div>
                    </div>
                  ) : null}

                  <Button
                    onClick={() => handleSaveTypeDetails(type)}
                    isLoading={updateStringer.isPending || updateCoach.isPending || updateAcademy.isPending}
                  >
                    {profile.providerTypes.includes(type) ? `Save ${getTypeLabel(type)}` : `Add ${getTypeLabel(type)}`}
                  </Button>
                </div>
              ))}

              {availableTypes.length ? (
                <div className="border-t border-border pt-4">
                  <SectionKicker title="Add another service" />
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    {availableTypes.map((option) => (
                      <button
                        key={option.type}
                        type="button"
                        onClick={() => setStagedTypes((current) => (current.includes(option.type) ? current : [...current, option.type]))}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface px-4 py-3 text-left transition-colors hover:border-primary/30"
                      >
                        <span className="font-semibold text-text">{option.label}</span>
                        <Plus className="h-4 w-4 text-primary" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <ProviderWorkspaceCard
            profile={profile}
            linkedMembership={linkedTenantMembership}
            isSwitchingWorkspace={isSwitchingTenant}
            isRequestingWorkspace={requestCoachWorkspace.isPending}
            onOpenWorkspace={() => void handleOpenLinkedWorkspace('/welcome')}
            onOpenSchedule={() => void handleOpenLinkedWorkspace('/schedule')}
            onOpenPeople={() => void handleOpenLinkedWorkspace('/directory')}
            onRequestWorkspace={() => void handleRequestCoachWorkspace()}
          />

          <Card variant="default">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Publish checklist
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {profile.blockingIssues?.length ? (
                <ReadinessIssueList tone="danger" title="Blocking" items={profile.blockingIssues} />
              ) : null}
              {profile.warningIssues?.length ? (
                <ReadinessIssueList tone="warning" title="Review" items={profile.warningIssues} />
              ) : null}
              {!profile.blockingIssues?.length && !profile.warningIssues?.length ? (
                <div className="rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm font-semibold text-primary">
                  Ready for admin publish
                </div>
              ) : null}
              {healthChecks.map((check) => (
                <div
                  key={check.label}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-surface-elevated/30 px-4 py-3"
                >
                  <span className="text-sm text-text">{check.label}</span>
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold ${check.ready ? 'text-primary' : 'text-text-muted'}`}>
                    {check.ready ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                    {check.ready ? 'Ready' : 'Needs attention'}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function SectionKicker({ title }: { title: string }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">
      {title}
    </p>
  );
}

function ReadinessIssueList({
  tone,
  title,
  items,
}: {
  tone: 'danger' | 'warning';
  title: string;
  items: string[];
}) {
  const toneClass =
    tone === 'danger'
      ? 'border-red-200 bg-red-50 text-red-700'
      : 'border-amber-300 bg-amber-50 text-amber-800';

  return (
    <div className={`rounded-2xl border px-4 py-3 ${toneClass}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.16em]">{title}</p>
      <div className="mt-3 space-y-2">
        {items.map((item) => (
          <div key={item} className="flex items-start gap-2 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProviderReviewWorkflow({ profile }: { profile: ProviderProfile }) {
  const activeStepIndex = getProviderWorkflowStepIndex(profile.publicationStatus);
  const isClosed = profile.publicationStatus === 'ARCHIVED';
  const steps = ['Request', 'Review', 'Approved', 'Live'];

  return (
    <div className="grid gap-2 sm:grid-cols-4">
      {steps.map((step, index) => {
        const isComplete = !isClosed && (profile.publicationStatus === 'PUBLISHED' || index < activeStepIndex);
        const isCurrent = index === activeStepIndex && !isComplete;
        const isProblem = isCurrent && (profile.publicationStatus === 'SUSPENDED' || profile.publicationStatus === 'ARCHIVED');

        return (
          <div
            key={step}
            className={`rounded-lg border px-3 py-3 ${
              isComplete
                ? 'border-primary/20 bg-primary/10'
                : isProblem
                  ? 'border-amber-300 bg-amber-50'
                  : isCurrent
                    ? 'border-primary/30 bg-surface'
                    : 'border-border bg-surface'
            }`}
          >
            <div className={`mb-2 ${isComplete ? 'text-primary' : isProblem ? 'text-amber-700' : 'text-text-muted'}`}>
              {isComplete ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : isProblem ? (
                <AlertCircle className="h-4 w-4" />
              ) : (
                <Clock3 className="h-4 w-4" />
              )}
            </div>
            <p className="text-sm font-semibold text-text">{step}</p>
          </div>
        );
      })}
    </div>
  );
}

function ProviderWorkspaceCard({
  profile,
  linkedMembership,
  isSwitchingWorkspace,
  isRequestingWorkspace,
  onOpenWorkspace,
  onOpenSchedule,
  onOpenPeople,
  onRequestWorkspace,
}: {
  profile: ProviderProfile;
  linkedMembership: TenantMembershipSummary | null;
  isSwitchingWorkspace: boolean;
  isRequestingWorkspace: boolean;
  onOpenWorkspace: () => void;
  onOpenSchedule: () => void;
  onOpenPeople: () => void;
  onRequestWorkspace: () => void;
}) {
  const hasTenantLink = Boolean(profile.tenantId);
  const tenantName = linkedMembership?.tenant?.displayName || 'Business workspace';
  const workLabel = linkedMembership?.tenant?.workTabLabel || 'Schedule';
  const directoryLabel = linkedMembership?.tenant?.tenantArchetype === 'COACHING'
    ? 'Students'
    : linkedMembership?.tenant?.directoryTabLabel || 'People';
  const canOpenWorkspace = Boolean(linkedMembership?.tenant?.id);
  const isCoachListing = profile.providerTypes.some((type) => ['COACH', 'STRINGER', 'ACADEMY'].includes(type));
  const workspaceStatus = profile.workspace?.status || (canOpenWorkspace ? 'ENABLED' : 'NOT_REQUESTED');
  const canRequestWorkspace =
    isCoachListing &&
    ['SUBMITTED', 'VERIFIED', 'PUBLISHED'].includes(profile.publicationStatus || '') &&
    !hasTenantLink &&
    workspaceStatus === 'NOT_REQUESTED';
  const workspaceRequested = !hasTenantLink && ['REQUESTED', 'APPROVED'].includes(workspaceStatus);

  return (
    <Card variant="default">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowRightLeft className="h-5 w-5 text-primary" />
          Business workspace
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-border bg-surface px-4 py-4">
          <p className="text-sm font-semibold text-text">
            {canOpenWorkspace
              ? tenantName
              : hasTenantLink
                ? 'Business workspace access needed'
                : workspaceRequested
                  ? 'Business workspace request sent'
                  : 'Business workspace not enabled'}
          </p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-text-subtle">
            {canOpenWorkspace
              ? 'Private tools ready'
              : workspaceRequested
                ? 'Waiting for TARK admin'
                : `${profile.publiclyVisible ? 'Public Profile live' : 'Public Profile first'}`}
          </p>
        </div>

        {canOpenWorkspace ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <Button onClick={onOpenWorkspace} isLoading={isSwitchingWorkspace}>
              Open Business workspace
            </Button>
            <Button variant="outline" onClick={onOpenSchedule} isLoading={isSwitchingWorkspace}>
              {workLabel}
            </Button>
            <Button variant="outline" onClick={onOpenPeople} isLoading={isSwitchingWorkspace}>
              {directoryLabel}
            </Button>
          </div>
        ) : hasTenantLink ? (
          <div className="flex flex-wrap gap-3">
            <Link to="/join-organization">
              <Button variant="outline">Join Organization</Button>
            </Link>
          </div>
        ) : canRequestWorkspace ? (
          <div className="flex flex-wrap gap-3">
            <Button onClick={onRequestWorkspace} isLoading={isRequestingWorkspace}>
              Request Business workspace
            </Button>
          </div>
        ) : workspaceRequested ? (
          <div className="rounded-lg border border-border bg-surface px-4 py-4 text-sm font-semibold text-text">
            Request pending
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <WorkspaceStatePill
              label="Public Profile"
              value={profile.publiclyVisible ? 'Public' : 'Private'}
              ready={Boolean(profile.publiclyVisible)}
            />
            <WorkspaceStatePill
              label="Business workspace"
              value={getWorkspaceStatusLabel(workspaceStatus)}
              ready={false}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WorkspaceStatePill({
  label,
  value,
  ready,
}: {
  label: string;
  value: string;
  ready: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">{label}</p>
      <p className={ready ? 'mt-2 font-semibold text-primary' : 'mt-2 font-semibold text-text'}>
        {value}
      </p>
    </div>
  );
}
